import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Workspace } from '@/api/types/workspace.types'

/**
 * 处理邀请链接的工具函数
 * 从API返回的链接中提取code参数，并使用当前host重新构建链接
 * @param apiUrl API返回的完整邀请链接
 * @returns 处理后的邀请链接
 */
const processInviteUrl = (apiUrl: string): string => {
  console.log('开始处理邀请链接:', apiUrl)

  try {
    // 解析API返回的URL
    const url = new URL(apiUrl)
    const code = url.searchParams.get('code')

    if (!code) {
      console.warn('无法从邀请链接中提取code参数:', apiUrl)
      return apiUrl // 如果提取失败，返回原链接
    }

    // 使用当前页面的host构建新的邀请链接
    const currentHost = window.location.origin
    const newInviteUrl = `${currentHost}/workspace/invite?code=${code}`

    console.log('邀请链接处理完成:', {
      原链接: apiUrl,
      提取的code: code,
      当前host: currentHost,
      新链接: newInviteUrl,
    })

    return newInviteUrl
  } catch (error) {
    console.error('处理邀请链接时出错:', error, '原链接:', apiUrl)
    return apiUrl // 如果处理失败，返回原链接
  }
}

interface WorkspaceState {
  currentWorkspace: Workspace | null
  workspaces: Workspace[]
  inviteUrls: Record<string, string> // 工作区ID -> 邀请链接的映射
  setCurrentWorkspace: (currentWorkspace: Workspace | null) => void
  setWorkspaces: (workspaces: Workspace[]) => void
  setInviteUrl: (workspaceId: string, inviteUrl: string) => void
  getInviteUrl: (workspaceId: string) => string | undefined
  clearInviteUrl: (workspaceId: string) => void
  reset: () => void
}

const initialState = {
  currentWorkspace: null,
  workspaces: [],
  inviteUrls: {},
}

export const useWorkspaceStore = create<WorkspaceState>()(
  persist(
    (set, get) => ({
      ...initialState,
      setCurrentWorkspace: (currentWorkspace) => set({ currentWorkspace }),
      setWorkspaces: (workspaces) => set({ workspaces }),
      setInviteUrl: (workspaceId, inviteUrl) => {
        // 处理邀请链接，提取code并重新构建
        const processedUrl = processInviteUrl(inviteUrl)
        set((state) => ({
          inviteUrls: {
            ...state.inviteUrls,
            [workspaceId]: processedUrl,
          },
        }))
      },
      getInviteUrl: (workspaceId) => get().inviteUrls[workspaceId],
      clearInviteUrl: (workspaceId) =>
        set((state) => {
          const { [workspaceId]: _, ...remainingUrls } = state.inviteUrls
          return { inviteUrls: remainingUrls }
        }),
      reset: () => set(initialState),
    }),
    {
      name: 'workspace-store', // 本地存储的key
      partialize: (state) => ({
        currentWorkspace: state.currentWorkspace,
        inviteUrls: state.inviteUrls,
      }), // 持久化currentWorkspace和inviteUrls
    }
  )
)
